import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Heart, MessageCircle, Mic2, BookOpen, Wind, Plus, Trash2, X,
  Play, Pause, RotateCcw, Edit3, ChevronRight, Clock, Smile, Battery, Users, Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =========================================================
   Social Battery section
   ========================================================= */
function SocialBattery() {
  const [entries, setEntries] = useState([]);
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [before, setBefore] = useState(7);
  const [setting, setSetting] = useState('');
  const [duration, setDuration] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/social/battery`, { withCredentials: true });
      setEntries(data.entries || []);
      setInsights(data.insights || {});
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEntry = async () => {
    try {
      await axios.post(`${API}/social/battery`, {
        before: Number(before),
        setting: setting || null,
        duration_min: duration ? Number(duration) : null,
      }, { withCredentials: true });
      toast.success('🔋 Sparat — kom tillbaka efter mötet');
      setShowForm(false); setSetting(''); setDuration(''); setBefore(7);
      load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const completeEntry = async (id, val) => {
    try {
      await axios.patch(`${API}/social/battery/${id}`, { after: Number(val) }, { withCredentials: true });
      toast.success('💛 Tack — det här hjälper hjärnan att se mönster');
      load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort?')) return;
    try {
      await axios.delete(`${API}/social/battery/${id}`, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const pendingEntry = entries.find((e) => e.after == null);

  return (
    <div className="neo-card p-4 bg-white" data-testid="social-battery">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-bold flex items-center gap-2">
          <Battery className="w-4 h-4" /> Sociala batteriet
        </h3>
        {!pendingEntry && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="neo-btn px-2.5 py-1 bg-[#FFE8A3] font-bold text-xs flex items-center gap-1"
            data-testid="battery-add-btn"
          >
            {showForm ? <><X className="w-3 h-3" /> Stäng</> : <><Plus className="w-3 h-3" /> Före möte</>}
          </button>
        )}
      </div>
      <p className="text-[11px] opacity-70 italic mb-3">
        Markera batteri-nivån före och efter möten — du ser med tiden vilka möten som dränerar och vilka som ger energi 💛
      </p>

      {/* Pending — needs after-rating */}
      {pendingEntry && (
        <div className="border-[3px] border-black rounded-xl p-3 bg-[#FDF0D5] mb-3" data-testid="battery-pending">
          <p className="text-xs font-bold mb-1">📍 Hur är det nu?</p>
          <p className="text-[10px] opacity-70 mb-2">
            {pendingEntry.setting && <span>Sätting: <strong>{pendingEntry.setting}</strong> · </span>}
            Före: <strong>{pendingEntry.before}/10</strong>
          </p>
          <div className="grid grid-cols-10 gap-1">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button
                key={n}
                onClick={() => completeEntry(pendingEntry.id, n)}
                className="neo-btn p-1.5 bg-white text-xs font-bold hover:bg-[#FFC8DD]"
                data-testid={`battery-after-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
          <p className="text-[10px] opacity-50 italic mt-1">1 = helt slut · 10 = full</p>
        </div>
      )}

      {/* New entry form */}
      {showForm && !pendingEntry && (
        <div className="border-2 border-black rounded-xl p-3 bg-[#FFF5E6] space-y-2 mb-3" data-testid="battery-form">
          <div>
            <label className="text-xs font-bold block mb-1">Hur är batteriet just nu? <span className="text-base">{before}/10</span></label>
            <input type="range" min="1" max="10" step="1" value={before} onChange={(e) => setBefore(e.target.value)} className="w-full accent-black" data-testid="battery-before-slider" />
          </div>
          <input type="text" value={setting} onChange={(e) => setSetting(e.target.value)} placeholder="Sätting (jobb, vänner, okända, familj...)" className="neo-input w-full text-sm" maxLength={60} data-testid="battery-setting-input" />
          <div className="flex items-center gap-2">
            <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Min" className="neo-input w-20 text-sm" min="0" data-testid="battery-duration-input" />
            <span className="text-xs opacity-60">minuter (valfritt)</span>
            <button onClick={startEntry} className="neo-btn ml-auto px-3 py-1.5 bg-[#B9FBC0] font-bold text-sm" data-testid="battery-save-btn">
              Spara
            </button>
          </div>
        </div>
      )}

      {/* Insights */}
      {(insights.total_completed || 0) > 0 && (
        <div className="border-2 border-black rounded-xl p-2.5 bg-gradient-to-br from-[#E8F5FF] to-[#FFE8F5] mb-3" data-testid="battery-insights">
          <p className="text-[10px] font-bold uppercase opacity-60 tracking-wider mb-1">Insikter</p>
          <p className="text-xs">
            Genomsnittlig drain: <strong>{insights.avg_drain}</strong> poäng över {insights.total_completed} möten
          </p>
          {Object.keys(insights.drain_by_setting || {}).length > 0 && (
            <div className="text-[11px] mt-1.5 space-y-0.5">
              {Object.entries(insights.drain_by_setting).map(([s, v]) => (
                <div key={s} className="flex justify-between">
                  <span className="capitalize">{s}</span>
                  <span className="font-bold">{v > 0 ? '−' : '+'}{Math.abs(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent list */}
      {loading ? null : entries.length === 0 ? (
        <p className="text-xs opacity-60 text-center py-2 italic">Inga registrerade möten ännu.</p>
      ) : (
        <div className="space-y-1" data-testid="battery-list">
          {entries.slice(0, 6).map((e) => {
            const drain = e.after != null ? e.before - e.after : null;
            return (
              <div key={e.id} className="flex items-center gap-2 border-2 border-black rounded-lg p-2 bg-white text-xs" data-testid={`battery-entry-${e.id}`}>
                <div className="w-7 h-7 rounded-full border-2 border-black bg-[#FFE8A3] flex items-center justify-center font-black shrink-0">
                  {e.before}
                </div>
                <span className="opacity-60">→</span>
                <div className={`w-7 h-7 rounded-full border-2 border-black flex items-center justify-center font-black shrink-0 ${e.after == null ? 'bg-white' : drain > 3 ? 'bg-[#FFB3BA]' : drain > 0 ? 'bg-[#FFE8A3]' : 'bg-[#B9FBC0]'}`}>
                  {e.after ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{e.setting || 'okategoriserat'}</div>
                  <div className="text-[10px] opacity-60">
                    {new Date(e.created_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                    {e.duration_min ? ` · ${e.duration_min} min` : ''}
                    {drain != null ? ` · drain ${drain > 0 ? '−' : '+'}${Math.abs(drain)}` : ''}
                  </div>
                </div>
                <button onClick={() => remove(e.id)} className="neo-btn p-1 bg-white" data-testid={`battery-delete-${e.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Person Cards (memory hooks)
   ========================================================= */
function PersonCards() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [hooksText, setHooksText] = useState('');
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('');

  const reset = () => { setEditing(null); setName(''); setRelation(''); setHooksText(''); setNotes(''); };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/social/people`, { withCredentials: true });
      setPeople(data.people || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => {
    setEditing(p);
    setName(p.name); setRelation(p.relation || ''); setHooksText((p.hooks || []).join('\n')); setNotes(p.notes || '');
    setShowForm(true);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Namn krävs');
      return;
    }
    const hooks = hooksText.split('\n').map(s => s.trim()).filter(Boolean);
    try {
      if (editing) {
        await axios.patch(`${API}/social/people/${editing.id}`, { name, relation: relation || null, hooks, notes: notes || null }, { withCredentials: true });
        toast.success('Uppdaterad 💛');
      } else {
        await axios.post(`${API}/social/people`, { name, relation: relation || null, hooks, notes: notes || null }, { withCredentials: true });
        toast.success('🌸 Lagt till');
      }
      reset(); setShowForm(false); load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort personkortet?')) return;
    try {
      await axios.delete(`${API}/social/people/${id}`, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const visible = filter
    ? people.filter(p =>
        p.name.toLowerCase().includes(filter.toLowerCase()) ||
        (p.relation || '').toLowerCase().includes(filter.toLowerCase()) ||
        (p.hooks || []).some(h => h.toLowerCase().includes(filter.toLowerCase()))
      )
    : people;

  return (
    <div className="neo-card p-4 bg-white" data-testid="person-cards">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-bold flex items-center gap-2">
          <Users className="w-4 h-4" /> Personkort (memory hooks)
        </h3>
        <button
          onClick={() => { reset(); setShowForm(!showForm); }}
          className="neo-btn px-2.5 py-1 bg-[#C8B6FF] font-bold text-xs flex items-center gap-1"
          data-testid="person-add-btn"
        >
          {showForm ? <><X className="w-3 h-3" /> Stäng</> : <><Plus className="w-3 h-3" /> Ny person</>}
        </button>
      </div>
      <p className="text-[11px] opacity-70 italic mb-3">
        Spara små fakta om personer du träffat — barn, hund, projekt. Nästa gång kan du säga "Hur var det med Lina?" och bli ihågkommen som omtänksam 💛
      </p>

      {showForm && (
        <div className="border-2 border-black rounded-xl p-3 bg-[#F5EEFF] space-y-2 mb-3" data-testid="person-form">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Namn" className="neo-input w-full text-sm" maxLength={80} data-testid="person-name-input" />
          <input type="text" value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="Relation (kollega, granne, vän, syster till...)" className="neo-input w-full text-sm" maxLength={60} data-testid="person-relation-input" />
          <textarea
            value={hooksText} onChange={(e) => setHooksText(e.target.value)}
            placeholder={"Memory hooks — en per rad, t.ex.:\nbarn Lina 4 år\nhund Bella\nprojekt om hållbarhet"}
            rows={4} className="neo-input w-full text-sm font-mono"
            data-testid="person-hooks-input"
          />
          <textarea
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Anteckningar (var ni träffades, vad ni pratade om...)"
            rows={2} className="neo-input w-full text-sm" maxLength={1000}
            data-testid="person-notes-input"
          />
          <button onClick={submit} className="neo-btn w-full py-1.5 bg-[#B9FBC0] font-bold text-sm" data-testid="person-save-btn">
            {editing ? 'Spara ändringar' : 'Lägg till'}
          </button>
        </div>
      )}

      {people.length > 3 && (
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="🔍 Sök på namn, relation eller hook..."
          className="neo-input w-full text-sm mb-2"
          data-testid="person-filter"
        />
      )}

      {loading ? null : visible.length === 0 ? (
        <p className="text-xs opacity-60 text-center py-2 italic">
          {people.length === 0 ? 'Inga personer ännu. Lägg till nästa du träffar 🌸' : 'Inget matchar sökningen.'}
        </p>
      ) : (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {visible.map((p) => (
            <div key={p.id} className="border-2 border-black rounded-xl p-2.5 bg-[#FFF5E6]" data-testid={`person-card-${p.id}`}>
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-full border-2 border-black bg-[#FFC8DD] flex items-center justify-center text-base font-black shrink-0">
                  {p.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm leading-tight">{p.name}</h4>
                  {p.relation && <p className="text-[10px] opacity-60">{p.relation}</p>}
                  {(p.hooks || []).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {p.hooks.map((h, i) => (
                        <li key={i} className="text-[11px] flex gap-1.5">
                          <span className="opacity-50">•</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {p.notes && <p className="text-[10px] italic opacity-70 mt-1.5 leading-snug">{p.notes}</p>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => startEdit(p)} className="neo-btn p-1 bg-white" data-testid={`person-edit-${p.id}`}>
                    <Edit3 className="w-3 h-3" />
                  </button>
                  <button onClick={() => remove(p.id)} className="neo-btn p-1 bg-white" data-testid={`person-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Pre-meeting 60s ritual modal
   ========================================================= */
function PreMeetingRitual({ open, onClose, ritual }) {
  const [idx, setIdx] = useState(-1);
  const [remaining, setRemaining] = useState(0);
  const [playing, setPlaying] = useState(false);
  const tickRef = useRef(null);

  const reset = () => { setIdx(-1); setRemaining(0); setPlaying(false); };
  useEffect(() => { if (!open) reset(); }, [open]);

  useEffect(() => {
    if (!playing) return;
    tickRef.current = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(tickRef.current);
  }, [playing, idx]);

  useEffect(() => {
    if (!playing || remaining !== 0) return;
    const steps = ritual?.steps || [];
    if (idx < 0) {
      setIdx(0); setRemaining(steps[0].duration);
    } else if (idx < steps.length - 1) {
      const next = idx + 1;
      setIdx(next); setRemaining(steps[next].duration);
    } else {
      setPlaying(false);
      setIdx(steps.length); // finished marker
    }
  }, [remaining, playing]);  // eslint-disable-line

  if (!open || !ritual) return null;
  const steps = ritual.steps || [];
  const finished = idx >= steps.length;
  const cur = idx >= 0 && idx < steps.length ? steps[idx] : null;

  const start = () => {
    if (idx >= steps.length) reset();
    if (idx === -1) { setIdx(0); setRemaining(steps[0].duration); }
    setPlaying(true);
  };

  return (
    <div className="fixed inset-0 z-[210] bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF] overflow-auto" data-testid="premeeting-modal">
      <div className="sticky top-0 bg-white/70 backdrop-blur-md border-b-[3px] border-black p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wind className="w-5 h-5" />
          <div>
            <div className="font-black text-sm">{ritual.title}</div>
            <div className="text-[10px] opacity-60">{ritual.subtitle}</div>
          </div>
        </div>
        <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="premeeting-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="max-w-md mx-auto p-4 pb-20 space-y-3">
        {idx === -1 && (
          <div className="neo-card p-5 bg-white text-center space-y-3" data-testid="premeeting-intro">
            <div className="text-5xl">🌬️</div>
            <p className="text-sm">Du behöver inte vara perfekt för att vara välkommen.<br/>Bara landa i kroppen i 60 sek först.</p>
            <button onClick={start} className="neo-btn px-5 py-2.5 bg-[#B9FBC0] font-bold" data-testid="premeeting-start">
              <Play className="w-4 h-4 inline mr-1" /> Starta
            </button>
          </div>
        )}
        {cur && (
          <div className="neo-card p-5 bg-white text-center space-y-3" data-testid="premeeting-step">
            <div className="text-6xl">{cur.emoji}</div>
            <h3 className="font-black text-xl">{cur.label}</h3>
            <div className="w-20 h-20 mx-auto rounded-full border-[4px] border-black flex items-center justify-center bg-gradient-to-br from-[#FFE8A3] to-[#FFC8DD] shadow-[3px_3px_0_#000]">
              <span className="text-2xl font-black">{remaining}s</span>
            </div>
            <p className="text-sm leading-relaxed">{cur.cue}</p>
            <div className="flex justify-center gap-2 pt-2">
              <button onClick={() => setPlaying(!playing)} className="neo-btn px-4 py-2 bg-[#C8B6FF] font-bold flex items-center gap-1">
                {playing ? <><Pause className="w-4 h-4" /> Paus</> : <><Play className="w-4 h-4" /> Fortsätt</>}
              </button>
              <button onClick={reset} className="neo-btn px-3 py-2 bg-white" title="Börja om">
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[11px] opacity-60">Steg {idx + 1} / {steps.length}</div>
          </div>
        )}
        {finished && (
          <div className="neo-card p-5 bg-gradient-to-br from-[#B9FBC0] to-[#FFC8DD] text-center space-y-3" data-testid="premeeting-done">
            <div className="text-5xl">🌸</div>
            <h3 className="font-black text-lg">Du är redo</h3>
            <p className="text-sm italic">Andas. Le med ögonen. Fråga något. Lyssna.<br/>Det räcker.</p>
            <button onClick={onClose} className="neo-btn px-5 py-2.5 bg-white font-bold">Gå ut i världen 💛</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   Story Bank section
   ========================================================= */
function StoryBank() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [hook, setHook] = useState('');
  const [body, setBody] = useState('');
  const [lengthSec, setLengthSec] = useState(30);

  const reset = () => { setEditing(null); setTitle(''); setHook(''); setBody(''); setLengthSec(30); };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/social/stories`, { withCredentials: true });
      setItems(data.stories || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (s) => {
    setEditing(s);
    setTitle(s.title); setHook(s.hook || ''); setBody(s.body); setLengthSec(s.length_sec || 30);
    setShowForm(true);
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Titel och berättelsetext krävs');
      return;
    }
    try {
      if (editing) {
        await axios.patch(`${API}/social/stories/${editing.id}`, {
          title, hook: hook || null, body, length_sec: Number(lengthSec) || 30,
        }, { withCredentials: true });
        toast.success('Sparad 💛');
      } else {
        await axios.post(`${API}/social/stories`, {
          title, hook: hook || null, body, length_sec: Number(lengthSec) || 30,
        }, { withCredentials: true });
        toast.success('🌸 Lagt till i story-banken');
      }
      reset(); setShowForm(false); load();
    } catch (e) {
      toast.error('Kunde inte spara');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort den här anekdoten?')) return;
    try {
      await axios.delete(`${API}/social/stories/${id}`, { withCredentials: true });
      load();
    } catch { toast.error('Kunde inte radera'); }
  };

  return (
    <div className="neo-card p-4 bg-white" data-testid="story-bank">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-bold flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> Story-banken
        </h3>
        <button
          onClick={() => { reset(); setShowForm(!showForm); }}
          className="neo-btn px-2.5 py-1 bg-[#FFC8DD] font-bold text-xs flex items-center gap-1"
          data-testid="story-add-btn"
        >
          {showForm ? <><X className="w-3 h-3" /> Stäng</> : <><Plus className="w-3 h-3" /> Ny</>}
        </button>
      </div>
      <p className="text-[11px] opacity-70 mb-3 italic">
        Förbered 4–6 korta anekdoter (~30 sek) du kan dra fram när samtalet stannar. Det är inte fusk — det är trygghet.
      </p>

      {showForm && (
        <div className="border-[2px] border-black rounded-xl p-3 bg-[#FDF0D5] space-y-2 mb-3" data-testid="story-form">
          <input
            type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Titel (för dig själv: t.ex. 'Den med grodan')"
            className="neo-input w-full text-sm" maxLength={120}
            data-testid="story-title-input"
          />
          <input
            type="text" value={hook} onChange={(e) => setHook(e.target.value)}
            placeholder="Hook — första meningen som drar in lyssnaren"
            className="neo-input w-full text-sm" maxLength={200}
            data-testid="story-hook-input"
          />
          <textarea
            value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Berättelsen — i scen, med detaljer, max ~30 sek högt"
            rows={4} className="neo-input w-full text-sm" maxLength={1500}
            data-testid="story-body-input"
          />
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 opacity-60" />
            <input
              type="number" value={lengthSec} onChange={(e) => setLengthSec(e.target.value)}
              min="10" max="120" step="5"
              className="neo-input w-20 text-sm text-right"
              data-testid="story-length-input"
            />
            <span className="text-xs opacity-60">sek högt</span>
            <button onClick={submit} className="neo-btn ml-auto px-4 py-1.5 bg-[#B9FBC0] font-bold text-sm" data-testid="story-save-btn">
              {editing ? 'Spara ändringar' : 'Lägg till'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm opacity-50 text-center py-4">Laddar...</p>
      ) : items.length === 0 ? (
        <p className="text-xs opacity-60 text-center py-3 italic">
          Inga anekdoter ännu. Börja med en — t.ex. "Sista gången jag skrattade högt".
        </p>
      ) : (
        <div className="space-y-1.5">
          {items.map((s) => (
            <div key={s.id} className="border-2 border-black rounded-xl p-2.5 bg-[#FFF5E6]" data-testid={`story-item-${s.id}`}>
              <div className="flex items-start gap-2">
                <span className="text-xl shrink-0">📖</span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm">{s.title}</h4>
                  {s.hook && <p className="text-[11px] italic opacity-80 mt-0.5">"{s.hook}"</p>}
                  <p className="text-xs opacity-80 mt-1 leading-snug whitespace-pre-wrap">{s.body}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] bg-white border border-black rounded-full px-1.5 py-0.5">⏱️ {s.length_sec}s</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => startEdit(s)} className="neo-btn p-1 bg-white" data-testid={`story-edit-${s.id}`} title="Redigera">
                    <Edit3 className="w-3 h-3" />
                  </button>
                  <button onClick={() => remove(s.id)} className="neo-btn p-1 bg-white" data-testid={`story-delete-${s.id}`} title="Ta bort">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Debrief modal
   ========================================================= */
function DebriefModal({ open, onClose, questions }) {
  const [answers, setAnswers] = useState({});
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 overflow-auto" onClick={onClose} data-testid="debrief-modal">
      <div className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold flex items-center gap-2">
            <Heart className="w-4 h-4" /> Efter mötet
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="debrief-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] opacity-70 italic mb-3">
          Hjärnan vill katastrofera direkt efter — låt oss styra om den. Inget måste vara perfekt skrivet.
        </p>
        <div className="space-y-3">
          {(questions || []).map((q, i) => (
            <div key={i} data-testid={`debrief-q-${i}`}>
              <label className="block text-sm font-bold mb-1">{q.emoji} {q.q}</label>
              <p className="text-[10px] opacity-60 mb-1">{q.hint}</p>
              <textarea
                value={answers[i] || ''}
                onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                rows={3}
                className="neo-input w-full text-sm"
                placeholder="Skriv fritt..."
                data-testid={`debrief-answer-${i}`}
              />
            </div>
          ))}
        </div>
        <button onClick={onClose} className="neo-btn w-full mt-3 py-2.5 bg-[#B9FBC0] font-bold" data-testid="debrief-done">
          Klar — släpp tankarna 💛
        </button>
        <p className="text-[10px] opacity-50 text-center italic mt-2">
          (Sparas inte — det är bara för att du ska få ut det ur huvudet.)
        </p>
      </div>
    </div>
  );
}

/* =========================================================
   Main SocialTab
   ========================================================= */
export default function SocialTab() {
  const [content, setContent] = useState(null);
  const [ritualOpen, setRitualOpen] = useState(false);
  const [debriefOpen, setDebriefOpen] = useState(false);
  const [activeTechnique, setActiveTechnique] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/social/content`, { withCredentials: true });
        setContent(data);
      } catch { /* ignore */ }
    })();
  }, []);

  if (!content) {
    return <div className="text-center py-8 text-sm opacity-60">Laddar...</div>;
  }

  const tech = activeTechnique
    ? content.elaine_techniques.find((t) => t.id === activeTechnique)
    : null;

  return (
    <div className="space-y-4 pb-6" data-testid="social-tab">
      {/* Hero */}
      <div className="neo-card p-5 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#FFC8DD] border-2 border-black flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-['Fredoka']">Möten 💫</h2>
            <p className="text-xs text-gray-700">Verktyg för att låta din värme komma fram snabbare</p>
          </div>
        </div>
        <p className="text-[11px] opacity-70 italic mt-2">
          Det här är inte att låtsas vara någon annan. Det är att hjälpa folk se den du redan är.
        </p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => setRitualOpen(true)}
          className="neo-btn px-3 py-3 bg-[#FFE8A3] font-bold flex items-center justify-center gap-2 text-sm"
          data-testid="open-premeeting-btn"
        >
          <Wind className="w-4 h-4" /> 60 sek innan
        </button>
        <button
          onClick={() => setDebriefOpen(true)}
          className="neo-btn px-3 py-3 bg-[#C8B6FF] font-bold flex items-center justify-center gap-2 text-sm"
          data-testid="open-debrief-btn"
        >
          <Heart className="w-4 h-4" /> Efter mötet
        </button>
      </div>

      {/* First seconds */}
      <div className="neo-card p-4 bg-white" data-testid="first-seconds-section">
        <h3 className="font-bold flex items-center gap-2 mb-1">
          <Smile className="w-4 h-4" /> {content.first_seconds.title}
        </h3>
        <p className="text-[11px] opacity-70 italic mb-3">{content.first_seconds.subtitle}</p>
        <ol className="space-y-2">
          {content.first_seconds.moves.map((m) => (
            <li key={m.order} className="flex items-start gap-2 border-l-[3px] border-black pl-2.5" data-testid={`first-second-${m.order}`}>
              <div className="w-7 h-7 rounded-full border-2 border-black bg-[#FFE8A3] flex items-center justify-center text-sm font-black shrink-0">
                {m.order}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-lg">{m.emoji}</span>
                  <span className="font-bold text-sm">{m.label}</span>
                </div>
                <p className="text-xs opacity-80 leading-snug">{m.tip}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Elaine techniques */}
      <div className="neo-card p-4 bg-white" data-testid="elaine-section">
        <h3 className="font-bold flex items-center gap-2 mb-1">
          <Mic2 className="w-4 h-4" /> Tala så folk lyssnar
        </h3>
        <p className="text-[11px] opacity-70 italic mb-3">
          Inspirerat av Elaine Eksvärds retorik — sök på pausen, etos, patos, logos.
        </p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {content.elaine_techniques.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTechnique(t.id)}
              className="neo-card p-2.5 bg-[#FDF0D5] hover:bg-[#FFE8A3] text-left flex items-start gap-2 transition"
              data-testid={`elaine-tech-${t.id}`}
            >
              <span className="text-2xl shrink-0">{t.emoji}</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm">{t.title}</h4>
                <p className="text-[11px] opacity-80 leading-snug">{t.core}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 opacity-50 shrink-0 mt-1" />
            </button>
          ))}
        </div>
      </div>

      {/* Listening strategies — your superpower */}
      <div className="neo-card p-4 bg-white" data-testid="listening-section">
        <h3 className="font-bold flex items-center gap-2 mb-1">
          👂 {content.listening_strategies?.title || 'Lyssnings-strategier'}
        </h3>
        <p className="text-[11px] opacity-70 italic mb-3">{content.listening_strategies?.subtitle}</p>
        <div className="grid gap-2">
          {(content.listening_strategies?.principles || []).map((p, i) => (
            <div key={i} className="border-2 border-black rounded-xl p-2.5 bg-[#E8F5FF]" data-testid={`listening-principle-${i}`}>
              <div className="flex items-start gap-2">
                <span className="text-2xl shrink-0">{p.emoji}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm">{p.title}</h4>
                  <p className="text-[11px] opacity-80 mt-0.5 leading-snug">{p.core}</p>
                  <p className="text-xs mt-1 italic border-l-2 border-black pl-2">{p.tip}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {(content.listening_strategies?.anti_patterns || []).length > 0 && (
          <div className="mt-3 border-2 border-black rounded-xl p-2.5 bg-[#FFE8E8]">
            <h4 className="text-[11px] font-bold uppercase opacity-70 tracking-wider mb-1">Det här undviker du</h4>
            <ul className="text-xs space-y-0.5">
              {content.listening_strategies.anti_patterns.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Smooth sharing */}
      <div className="neo-card p-4 bg-white" data-testid="sharing-section">
        <h3 className="font-bold flex items-center gap-2 mb-1">
          🎁 {content.smooth_sharing?.title || 'Dela med dig — smidigt'}
        </h3>
        <p className="text-[11px] opacity-70 italic mb-3">{content.smooth_sharing?.subtitle}</p>

        <div className="grid gap-1.5 mb-3">
          {(content.smooth_sharing?.rules || []).map((r, i) => (
            <div key={i} className="flex items-start gap-2 border-l-[3px] border-black pl-2.5" data-testid={`sharing-rule-${i}`}>
              <span className="text-xl shrink-0">{r.emoji}</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm">{r.title}</h4>
                <p className="text-[11px] opacity-80 leading-snug">{r.tip}</p>
              </div>
            </div>
          ))}
        </div>

        <h4 className="text-[10px] font-bold uppercase opacity-60 tracking-wider mb-1.5">Mallar (kopiera mönstret)</h4>
        <div className="grid gap-1.5">
          {(content.smooth_sharing?.templates || []).map((t, i) => (
            <div key={i} className="border-2 border-black rounded-xl p-2.5 bg-[#FDF0D5]" data-testid={`sharing-template-${i}`}>
              <div className="flex items-start gap-2">
                <span className="text-2xl shrink-0">{t.emoji}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm">{t.label}</h4>
                  <code className="text-[10px] block bg-white border border-black rounded px-1.5 py-0.5 mt-1 mb-1.5 font-mono opacity-80">
                    {t.structure}
                  </code>
                  <p className="text-xs italic leading-snug">{t.example}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conversation starters */}
      <div className="neo-card p-4 bg-white" data-testid="starters-section">
        <h3 className="font-bold flex items-center gap-2 mb-1">
          <MessageCircle className="w-4 h-4" /> {content.conversation_starters.title}
        </h3>
        <p className="text-[11px] opacity-70 italic mb-3">{content.conversation_starters.subtitle}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {content.conversation_starters.categories.map((c) => (
            <div key={c.id} className="border-2 border-black rounded-xl p-2.5 bg-[#FFF5E6]" data-testid={`starter-cat-${c.id}`}>
              <h4 className="font-bold text-sm mb-1.5 flex items-center gap-1.5">
                <span className="text-xl">{c.emoji}</span> {c.label}
              </h4>
              <ul className="space-y-1 text-xs">
                {c.questions.map((q, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="opacity-50">•</span>
                    <span>"{q}"</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bridges + exits */}
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="neo-card p-4 bg-white" data-testid="bridges-section">
          <h4 className="font-bold text-sm mb-1">{content.bridges.title}</h4>
          <p className="text-[10px] opacity-70 italic mb-2">{content.bridges.subtitle}</p>
          <ul className="space-y-1 text-xs">
            {content.bridges.phrases.map((p, i) => (
              <li key={i} className="border-l-2 border-black pl-2 py-0.5">"{p}"</li>
            ))}
          </ul>
        </div>
        <div className="neo-card p-4 bg-white" data-testid="exits-section">
          <h4 className="font-bold text-sm mb-1">{content.exit_lines.title}</h4>
          <p className="text-[10px] opacity-70 italic mb-2">{content.exit_lines.subtitle}</p>
          <ul className="space-y-1 text-xs">
            {content.exit_lines.phrases.map((p, i) => (
              <li key={i} className="border-l-2 border-black pl-2 py-0.5">"{p}"</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Power reminders */}
      <div className="neo-card p-4 bg-gradient-to-br from-[#FFD6A5] to-[#FFE8F5]" data-testid="power-reminders">
        <h4 className="font-bold text-sm mb-2">⚡ Snabba påminnelser i stunden</h4>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {content.power_reminders.map((r, i) => (
            <div key={i} className="bg-white border-2 border-black rounded-lg p-2 text-xs flex items-start gap-2">
              <span className="text-lg shrink-0">{r.emoji}</span>
              <span>{r.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Story bank */}
      <StoryBank />

      {/* Social battery */}
      <SocialBattery />

      {/* Person cards (memory hooks) */}
      <PersonCards />

      {/* Modals */}
      <PreMeetingRitual
        open={ritualOpen}
        onClose={() => setRitualOpen(false)}
        ritual={content.pre_meeting}
      />
      <DebriefModal
        open={debriefOpen}
        onClose={() => setDebriefOpen(false)}
        questions={content.debrief_questions}
      />

      {/* Technique deep-dive modal */}
      {tech && (
        <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setActiveTechnique(null)} data-testid="technique-modal">
          <div className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold flex items-center gap-2">
                <span className="text-2xl">{tech.emoji}</span> {tech.title}
              </h3>
              <button onClick={() => setActiveTechnique(null)} className="neo-btn p-1.5 bg-white" data-testid="technique-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm font-bold border-l-[3px] border-black pl-2 italic">"{tech.core}"</p>
            <div className="mt-3">
              <h4 className="text-[10px] font-bold uppercase opacity-60 tracking-wider mb-1">Så gör du</h4>
              <p className="text-sm leading-relaxed">{tech.how}</p>
            </div>
            <div className="mt-3 border-2 border-black rounded-xl p-2.5 bg-[#FDF0D5]">
              <h4 className="text-[10px] font-bold uppercase opacity-60 tracking-wider mb-1">Exempel</h4>
              <p className="text-sm italic">{tech.example}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
